<template>
    <div class="border border-indigo-200 bg-indigo-50 rounded-md p-4 flex flex-col gap-3">
        <div class="flex justify-between items-center gap-3">
            <h3 class="font-medium">
                {{ campaignGame.game_name }}
            </h3>

            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="resettingDefaults"
                    @click="showResetWarning = true"
                >
                    {{ resettingDefaults ? 'Resetting...' : 'Reset to Default' }}
                </button>
            </div>
        </div>

        <div
            v-if="showResetWarning"
            class="bg-white border border-red-200 rounded-md p-4 flex flex-col gap-3"
        >
            <p class="text-sm text-gray-700">
                This will reset all Roblox Placement IDs for this game to the default value.
                Existing placement IDs will be overwritten.
            </p>

            <div class="flex justify-end gap-2">
                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
                    :disabled="resettingDefaults"
                    @click="showResetWarning = false"
                >
                    Cancel
                </button>

                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="resettingDefaults"
                    @click="resetToDefaults"
                >
                    {{ resettingDefaults ? 'Resetting...' : 'Confirm Reset' }}
                </button>
            </div>
        </div>

        <div
            v-for="creative in campaignGame.campaign_game_creatives"
            :key="creative.id"
            class="grid grid-cols-1 md:grid-cols-2 gap-3 items-center bg-white border border-indigo-100 rounded-md p-3"
        >
            <div class="flex items-center gap-3 min-w-0">
                <img
                    v-if="creativeThumbnailUrl(creative)"
                    :src="creativeThumbnailUrl(creative)"
                    :alt="creative.creative_name || 'Creative thumbnail'"
                    class="w-16 h-16 rounded-md object-cover border border-gray-200 bg-gray-100 shrink-0"
                />

                <div
                    v-else
                    class="w-16 h-16 rounded-md border border-gray-200 bg-gray-100 text-gray-400 text-xs flex items-center justify-center shrink-0"
                >
                    No image
                </div>

                <div class="flex flex-col min-w-0">
                    <span class="font-medium truncate">
                        {{ creative.creative_name || 'Unnamed creative' }}
                    </span>

                    <span class="text-xs text-gray-500">
                        Creative ID: {{ creative.creative_id }}
                    </span>
                </div>
            </div>

            <div class="flex flex-col">
                <label class="text-xs text-gray-500 mb-1">
                    Roblox Placement ID
                </label>

                <input
                    type="text"
                    :value="creative.roblox_placement_id"
                    class="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    @input="$emit('update-placement-id', creative.id, $event.target.value)"
                />
            </div>
        </div>

        <div
            v-if="!campaignGame.campaign_game_creatives?.length"
            class="text-sm text-gray-500"
        >
            No creatives found for this game. Use Fill Defaults to create missing joins.
        </div>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    name: 'CampaignGameCreativeRobloxPlacementIds',

    props: {
        campaignGame: {
            type: Object,
            required: true,
        },
        fillingDefaults: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update-placement-id', 'fill-defaults', 'reset-defaults'],

    data() {
        return {
            showResetWarning: false,
            resettingDefaults: false,
        }
    },

    methods: {
        creativeThumbnailUrl(creative) {
            return creative.thumbnail_url || creative.img_url || ''
        },

        async resetToDefaults() {
            if (this.resettingDefaults) {
                return
            }

            this.resettingDefaults = true

            try {
                await axios.patch(`/campaign-games/${this.campaignGame.id}/creatives/reset-roblox-placement-ids`)

                this.showResetWarning = false
                this.$emit('reset-defaults')
            } catch (error) {
                console.error('Failed to reset Roblox placement IDs', error)
            } finally {
                this.resettingDefaults = false
            }
        },
    },
}
</script>